import { useState } from 'react';
import { LayoutDashboard, Bluetooth, Activity, Database, Cpu, FileText, Settings, ChevronLeft, X } from 'lucide-react';
import { useUIStore } from '../../stores/uiStore';
import type { MenuId } from '../../stores/uiStore';
import { useDeviceStatus } from '../../stores/systemStore';
import { LooxidIcon } from '../ui/LooxidIcon';
import { useTranslation } from '../../locales';
import { useLanguageStore } from '../../stores/languageStore';
import type { TranslationKeys } from '../../locales';

/**
 * Sidebar — 핸드오프 디자인 시스템의 sb__* 컨벤션을 따른다.
 *
 * 구조: brand → section header → item 리스트 → footer(연결 상태).
 * 활성 항목은 indigo tinted 배경 + 좌측 보더. 라이트/다크 모두 토큰만으로 적응.
 */
type SidebarItemKey = keyof Pick<
  TranslationKeys['sidebar'],
  'dashboard' | 'deviceManager' | 'visualizer' | 'dataCenter' | 'streamProcess' | 'documents' | 'settings'
>;
type SectionKey = keyof TranslationKeys['sidebar']['sections'];

type NavItem =
  | { kind: 'section'; sectionKey: SectionKey }
  | { kind: 'item'; id: MenuId; tKey: SidebarItemKey; Icon: typeof LayoutDashboard; badge?: string };

const NAV: NavItem[] = [
  { kind: 'section', sectionKey: 'workspace' },
  { kind: 'item', id: 'engine',          tKey: 'dashboard',     Icon: LayoutDashboard },
  { kind: 'item', id: 'linkband',        tKey: 'deviceManager', Icon: Bluetooth },
  { kind: 'item', id: 'visualizer',      tKey: 'visualizer',    Icon: Activity, badge: 'LIVE' },
  { kind: 'item', id: 'datacenter',      tKey: 'dataCenter',    Icon: Database },
  { kind: 'item', id: 'stream-process',  tKey: 'streamProcess', Icon: Cpu },
  { kind: 'section', sectionKey: 'reference' },
  { kind: 'item', id: 'cloudmanager',    tKey: 'documents',     Icon: FileText },
  { kind: 'item', id: 'settings',        tKey: 'settings',      Icon: Settings },
];

interface SidebarProps {
  collapsed?: boolean;
  onToggle?: () => void;
  isOpen?: boolean;
  onClose?: () => void;
}

export function Sidebar({ collapsed = false, onToggle, isOpen = false, onClose }: SidebarProps) {
  const { activeMenu, setActiveMenu } = useUIStore();
  const { isConnected } = useDeviceStatus();
  const { currentLanguage } = useLanguageStore();
  const t = useTranslation(currentLanguage);
  const [internalCollapsed, setInternalCollapsed] = useState(false);
  const isCollapsed = onToggle ? collapsed : internalCollapsed;
  const handleToggle = onToggle ?? (() => setInternalCollapsed((v) => !v));

  return (
    <>
      <div
        className={'sb-overlay' + (isOpen ? ' sb-overlay--visible' : '')}
        onClick={onClose}
        aria-hidden="true"
      />
    <aside className={'sb' + (isOpen ? ' sb--open' : '')}>
      {/* 모바일 닫기 버튼 */}
      {onClose && (
        <button
          type="button"
          className="sb__close-btn"
          onClick={onClose}
          aria-label={t.sidebar.closeLabel}
        >
          <X size={20} strokeWidth={1.5} />
        </button>
      )}
      {/* Brand */}
      <div className="sb__brand">
        <LooxidIcon size={26} className="text-white" />
        {!isCollapsed && (
          <div className="sb__word">
            <b>LINK BAND SDK</b>
          </div>
        )}
        <button
          type="button"
          onClick={handleToggle}
          aria-label={isCollapsed ? t.sidebar.expandLabel : t.sidebar.collapseLabel}
          className="lb-btn lb-btn--ghost lb-btn--sm"
          style={{ marginLeft: 'auto', padding: 6, borderColor: 'transparent' }}
        >
          <ChevronLeft size={14} style={{ transform: isCollapsed ? 'rotate(180deg)' : 'none', transition: 'transform 150ms' }} />
        </button>
      </div>

      {/* Nav */}
      <nav style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
        {NAV.map((entry, i) => {
          if (entry.kind === 'section') {
            return isCollapsed ? null : <div className="sb__sect" key={`s-${i}`}>{t.sidebar.sections[entry.sectionKey]}</div>;
          }
          const Icon = entry.Icon;
          const isActive = activeMenu === entry.id;
          const label = t.sidebar[entry.tKey];
          return (
            <button
              key={entry.id}
              type="button"
              onClick={() => {
                setActiveMenu(entry.id);
                onClose?.();
              }}
              className={'sb__item' + (isActive ? ' sb__item--active' : '')}
              style={isCollapsed ? { justifyContent: 'center', padding: '10px 0' } : undefined}
              title={isCollapsed ? label : undefined}
            >
              <Icon className="sb__icon" />
              {!isCollapsed && <span>{label}</span>}
              {!isCollapsed && entry.badge && <span className="sb__badge">{entry.badge}</span>}
            </button>
          );
        })}
      </nav>

      {/* Footer */}
      <div className="sb__footer">
        {isCollapsed ? (
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <span className={'lb-dot' + (isConnected ? '' : ' lb-dot--idle')} />
          </div>
        ) : (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span className={'lb-dot' + (isConnected ? '' : ' lb-dot--idle')} />
              <span>{isConnected ? t.sidebar.deviceConnected : t.sidebar.sdkReady}</span>
            </div>
            <div style={{ marginTop: 6, color: 'var(--fg-3)' }}>{t.sidebar.webBluetooth}</div>
            <div style={{ marginTop: 8, font: '500 10px/1.2 var(--lb-font-mono)', color: 'var(--fg-3)', letterSpacing: '0.08em', textTransform: 'uppercase' }}>v2.1.0</div>
          </>
        )}
      </div>
    </aside>
    </>
  );
}
